import type { ReactElement } from "react";
import { ArticleHero } from "../ArticleHero";
import familyHeroImage from "../../assets/images/family-fall-outfit-portrait.webp";
import family7 from "../../assets/images/mother-children-red-holiday-studio-portrait.webp"
import family8 from "../../assets/images/parents-baby-outdoor-family-portrait.webp"
import { ArticlePhoto } from "./ArticlePhoto";
import { UiIcon } from "./components/UiIcons";
import { GuideCtaActions } from "./components/GuideCtaActions";
import "./NewbornPages.scss";
import "./NewbornArticle.scss";
import "./FamilyArticle.scss";

const palettes = [
  { name: "Warm neutral", colors: ["#efe3d3", "#b99476", "#6f5a4a"] },
  { name: "Soft blue", colors: ["#d8e0df", "#8eaaa9", "#665b55"] },
  { name: "Earth and olive", colors: ["#d6c7aa", "#7d8062", "#9a6652"] },
  { name: "Muted rose", colors: ["#ead8d3", "#b4877b", "#75666a"] },
];

export const WhatToWearForFamilyPhotos = (): ReactElement => (
  <main className="newborn-page newborn-article-page family-article-page">
    <article>
      <ArticleHero
        breadcrumbs={[
          { label: "Home", to: "/" },
          { label: "Resources", to: "/resources/" },
          { label: "Family", to: "/resources/family/" },
          { label: "What to Wear for Family Photos" },
        ]}
        category="Family styling guide"
        description="Coordinate the people you love without matching exactly—and keep everyone comfortable enough to move and play."
        imageAlt="Family wearing coordinated neutral clothing for outdoor photographs"
        imagePosition="center 48%"
        imageSrc={familyHeroImage}
        readingTime="7 min read"
        title="What to Wear for Family Photos"
        updatedAt="2026-08-04"
        updatedLabel="Updated August 2026"
      />
      <div className="newborn-shell newborn-article-layout">
        <aside className="newborn-article-callout">
          <span className="newborn-article-callout__icon">
            <UiIcon name="star" />
          </span>
          <div>
            <h2>Quick answer</h2>
            <p>
              Choose two or three softened colors, vary their lightness across the family and mix natural textures.
              Coordinate rather than match. Start with the hardest person to dress, then build everyone else around that
              outfit.
            </p>
          </div>
        </aside>
        <nav aria-label="In this guide" className="newborn-article-toc">
          <h2>In this guide</h2>
          <ol>
            <li>
              <a href="#formula">The outfit formula</a>
            </li>
            <li>
              <a href="#start">Where to start</a>
            </li>
            <li>
              <a href="#texture">Texture and pattern</a>
            </li>
            <li>
              <a href="#children">Dress children</a>
            </li>
            <li>
              <a href="#avoid">What to avoid</a>
            </li>
            <li>
              <a href="#final">Final check</a>
            </li>
          </ol>
        </nav>
        <section className="newborn-article-section" id="formula">
          <p className="newborn-article-kicker">FOUR EASY DIRECTIONS</p>
          <h2>Family Outfit Formula</h2>
          <p>
            Use these as starting points, not uniforms. Repeat colors in small ways and let each person retain their own
            style.
          </p>
          <div className="family-outfit-formula">
            {palettes.map((palette) => (
              <article key={palette.name}>
                <div>
                  {palette.colors.map((color) => (
                    <i key={color} style={{ backgroundColor: color }} />
                  ))}
                </div>
                <h3>{palette.name}</h3>
              </article>
            ))}
          </div>
        </section>
        <section className="newborn-article-section newborn-article-split" id="start">
          <div>
            <p className="newborn-article-kicker">BUILD ONE LOOK AT A TIME</p>
            <h2>Start with the hardest outfit</h2>
            <p>
              This is often a parent’s dress, a meaningful piece or a child who has strong preferences. Once that outfit
              feels right, choose supporting colors for everyone else.
            </p>
            <p>
              Lay every complete look together—including shoes—to catch an accidental color clash or too many competing
              patterns.
            </p>
          </div>
          <ArticlePhoto
            alt="Mother and children wearing coordinated red outfits for holiday portraits"
            className="newborn-article-split__photo"
            label="PHOTO: OUTFIT PLAN"
            src={family7}
          />
        </section>
        <section className="newborn-article-section" id="texture">
          <p className="newborn-article-kicker">DEPTH WITHOUT DISTRACTION</p>
          <h2>Mix texture and use pattern sparingly</h2>
          <p>
            Knits, linen, denim, soft cotton and subtle layers add visual depth. One gentle pattern can anchor the
            palette; several bold patterns usually compete with faces and connection.
          </p>
        </section>
        <section className="newborn-article-section newborn-article-split newborn-article-split--reverse" id="children">
          <ArticlePhoto
            alt="Parents and baby wearing coordinated casual outfits outdoors"
            className="newborn-article-split__photo"
            label="PHOTO: CHILD-FRIENDLY"
            src={family8}
          />
          <div>
            <p className="newborn-article-kicker">LET THEM MOVE</p>
            <h2>Dress children for comfort</h2>
            <p>
              Children should be able to run, sit, cuddle and be lifted without clothing shifting uncomfortably. Avoid
              scratchy fabrics, stiff shoes and anything that requires repeated fixing.
            </p>
            <p>
              Offer two parent-approved options when a child wants a say. Feeling involved can make getting dressed much
              easier.
            </p>
          </div>
        </section>
        <section className="newborn-article-section" id="avoid">
          <p className="newborn-article-kicker">KEEP ATTENTION ON PEOPLE</p>
          <h2>What is usually best to avoid</h2>
          <ul className="newborn-article-checklist">
            <li>
              <UiIcon name="check" />
              <span>Large logos, neon colors and very high-contrast graphics.</span>
            </li>
            <li>
              <UiIcon name="check" />
              <span>Everyone wearing the same shirt and jeans.</span>
            </li>
            <li>
              <UiIcon name="check" />
              <span>Several unrelated statement patterns.</span>
            </li>
            <li>
              <UiIcon name="check" />
              <span>Uncomfortable new shoes or clothing that does not fit now.</span>
            </li>
          </ul>
        </section>
        <section className="newborn-article-section" id="final">
          <p className="newborn-article-kicker">TWO DAYS BEFORE</p>
          <h2>Do one complete final check</h2>
          <p>
            Try on every outfit, remove tags, steam clothing and photograph the complete group together. Pack a lint
            roller, a few pins and one simple backup for young children.
          </p>
        </section>
        <section className="newborn-article-cta family-article-cta">
          <div>
            <p className="newborn-article-kicker">A COHESIVE LOOK THAT FEELS LIKE YOU</p>
            <h2>Want a second opinion on your outfits?</h2>
            <p>Review the family package, or send me outfit photos if you are already booked and want a second opinion.</p>
          </div>
          <GuideCtaActions category="family" />
        </section>
      </div>
    </article>
  </main>
);
export default WhatToWearForFamilyPhotos;
